import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import { IconCodeBrackets } from 'central-icons/IconCodeBrackets';
import type React from 'react';

import { Button, Menu, MenuButton, MenuItem, MenuList } from '@revelio/ui';

import styles from './copy-as-menu.module.css';
import { useToast } from './hooks/useToast';
import {
  availableFormats,
  type RequestSpec,
  renderSnippet,
  SNIPPET_FORMATS,
  type SnippetFormat,
} from './request-snippets';

/** Copies the current request as a ready-to-run snippet; auth is always an env-var placeholder. */
export const CopyAsMenu: React.FC<{
  spec: RequestSpec;
  /** Fired once the snippet is on the clipboard. */
  onCopied?: (format: SnippetFormat) => void;
}> = ({ spec, onCopied }) => {
  const toast = useToast();
  const formats = availableFormats(spec);
  return (
    <Menu>
      <MenuButton>
        <Button size="xs" variant="outlined" data-testid="copy-as-menu">
          <div className={styles.triggerLabel}>
            <IconCodeBrackets size={13} />
            <span>Copy as</span>
            <IconChevronDownSmall size={12} />
          </div>
        </Button>
      </MenuButton>
      <MenuList minWidth={180}>
        {SNIPPET_FORMATS.filter((format) => formats.includes(format.id)).map(
          (format) => (
            <MenuItem
              key={format.id}
              data-testid={`copy-as-${format.id}`}
              onClick={() => {
                navigator.clipboard
                  .writeText(renderSnippet(format.id, spec))
                  .then(() => {
                    toast({
                      title: `Copied as ${format.label}`,
                      status: 'success',
                      duration: 1500,
                    });
                    onCopied?.(format.id);
                  })
                  .catch(() => {
                    toast({
                      title: 'Copy failed',
                      status: 'error',
                      duration: 2500,
                    });
                  });
              }}
            >
              {format.label}
            </MenuItem>
          )
        )}
      </MenuList>
    </Menu>
  );
};
